import { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Loader2, Lock, AtSign, AlertCircle, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/ui/LanguageSwitcher';
import MFAVerifyModal from '../components/auth/MFAVerifyModal';
import { getEnabledSsoProviders, signInWithSso, type EnabledSsoProvider, type SsoProviderType } from '../lib/queries/sso';
import { isAccountLocked, recordLoginAttempt } from '../lib/queries/auth-security';

export default function Login() {
  const { signIn, mfaPending, completeMfa, cancelMfa, authError, clearAuthError } = useAuth();
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ssoProviders, setSsoProviders] = useState<EnabledSsoProvider[]>([]);
  const [ssoLoading, setSsoLoading] = useState<string | null>(null);

  useEffect(() => {
    getEnabledSsoProviders().then(setSsoProviders).catch(() => {});
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Check if account is locked before attempting login
    try {
      const locked = await isAccountLocked(username);
      if (locked) {
        setError('Account is temporarily locked due to too many failed login attempts. Please try again later.');
        setLoading(false);
        return;
      }
    } catch {
      // If lockout check fails, proceed with login attempt
    }

    clearAuthError();
    const result = await signIn(username, password);
    if (result.error) {
      setError(result.error);
      recordLoginAttempt(username, false, result.error).catch(() => {});
    } else {
      recordLoginAttempt(username, true).catch(() => {});
    }
    setLoading(false);
  };

  // Show MFA verification modal if pending
  if (mfaPending) {
    return (
      <MFAVerifyModal
        factorId={mfaPending.factorId}
        onVerified={completeMfa}
        onCancel={cancelMfa}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 start-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 end-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="absolute top-4 end-4">
        <LanguageSwitcher />
      </div>

      <div className="w-full max-w-md relative animate-slide-up">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4 shadow-lg shadow-blue-500/25 overflow-hidden">
              <img src="/assets/logo.png" alt="Logo" className="w-full h-full object-contain p-1" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">{t('login_title')}</h1>
            <p className="text-slate-400 text-sm mt-1">{t('login_subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {(error || authError) && (
              <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-4 animate-fade-in">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-red-300 text-sm">{error || authError}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-300">{t('login_username')}</label>
              <div className="relative">
                <AtSign className="absolute start-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value.trim())}
                  required
                  autoComplete="username"
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-slate-500 rounded-xl ps-10 pe-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-mono"
                  placeholder={t('login_username_placeholder')}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-300">{t('login_password')}</label>
              <div className="relative">
                <Lock className="absolute start-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full bg-white/5 border border-white/10 text-white placeholder-slate-500 rounded-xl ps-10 pe-12 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  placeholder={t('login_password_placeholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute end-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-end">
                <Link to="/forgot-password" className="text-xs text-slate-400 hover:text-white transition-colors">
                  {t('login_forgot_password', 'Forgot password?')}
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-semibold py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('login_signing_in')}
                </>
              ) : (
                t('login_sign_in')
              )}
            </button>
          </form>

          {ssoProviders.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-center text-xs text-slate-500 mb-3">Or sign in with</p>
              <div className="space-y-2">
                {ssoProviders.map(provider => (
                  <button
                    key={provider.id}
                    type="button"
                    disabled={!!ssoLoading}
                    onClick={async () => {
                      setSsoLoading(provider.id);
                      try {
                        await signInWithSso(provider.provider_type);
                      } catch (err: unknown) {
                        setError(err instanceof Error ? err.message : 'SSO login failed');
                      }
                      setSsoLoading(null);
                    }}
                    className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white py-2.5 rounded-xl text-sm transition-all disabled:opacity-50"
                  >
                    {ssoLoading === provider.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Globe className="w-4 h-4 text-slate-400" />
                    )}
                    {provider.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-center text-xs text-slate-500">
              {t('login_modules')}
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-slate-600 mt-6">
          {t('login_copyright', { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  );
}
